<template>
  <div class="payment-container">
    <h4 class="title-responsive primary--text mb-4">
      {{ $tc('payment', 2) }}
    </h4>

    <!-- Formulario de pagos y listado -->
    <div class="payment-form">
      <!-- Formulario -->
      <ValidationObserver v-slot="{ handleSubmit }" ref="form">
        <form @submit.prevent="handleSubmit(submit)">
          <v-alert v-if="!subordersSelected.length" type="info" outlined>
            {{ $t('select_suborder') }}
          </v-alert>

          <!-- Métodos de pago -->
          <ValidationProvider
            vid="type"
            :name="$t('paymenttype')"
            rules="required"
            v-slot="{ errors }"
          >
            <v-autocomplete
              autocomplete="off"
              class="secondary--text"
              key="type-input"
              outlined
              :disabled="!subordersSelected.length"
              :error-messages="errors[0]"
              :items="types"
              item-text="name"
              item-value="pk"
              :label="$t('paymenttype')"
              :loading="loadingTypes"
              :prepend-inner-icon="checkPermissions.addNewType ? 'fa-plus' : ''"
              @click:prepend-inner="addPaymentType()"
              v-model="payment.type_payment"
            ></v-autocomplete>
          </ValidationProvider>

          <!-- Fecha -->
          <i-date-picker
            color="primary"
            :disabled="!subordersSelected.length"
            :label="$tc('date', 1)"
            :max="new Date().toISOString().slice(0, 10)"
            v-model="payment.date"
          />

          <!-- Monto -->
          <ValidationProvider
            vid="amount"
            :name="$tc('amount', 1)"
            :rules="{
              required: true,
              min_value: 0.01,
              max_value: Number(balance)
            }"
            v-slot="{ errors }"
          >
            <i-money
              :disabled="!subordersSelected.length"
              :error-messages="errors[0]"
              :label="$tc('amount', 1)"
              v-model="payment.amount"
            />
          </ValidationProvider>

          <!-- Referencia -->
          <v-text-field
            color="secondary"
            name="reference"
            outlined
            :disabled="!subordersSelected.length"
            :label="$tc('reference', 1)"
            v-model="payment.reference"
          ></v-text-field>

          <!-- Botón de guardar -->
          <div class="text-right">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <i-btn
                  color="primary"
                  icon="fa-save"
                  :disabled="!subordersSelected.length"
                  :loading="loadingSubmit"
                  :title="$tc('save', 1)"
                  @click="validateForm"
                  v-on="on"
                ></i-btn>
              </template>
            </v-tooltip>
          </div>
        </form>
      </ValidationObserver>

      <!-- Listado -->
      <div class="table-responsive">
        <v-data-table
          disable-sort
          :headers="headers"
          :items="payments"
          item-key="pk"
          :loading="loading"
        >
          <template v-slot:item.canceled="{ value }">
            <v-icon v-if="value" color="red" small>fa-times</v-icon>
            <v-icon v-else color="success" small>fa-check</v-icon>
          </template>
          <template v-slot:item.date="{ value }">
            {{ dateFormat(value) }}
          </template>
          <template v-slot:item.amount="{ value }">
            <i-money :value="value" mode="text" />
          </template>
          <template v-slot:item.allocations="{ value }">
            <ul class="mb-0">
              <li v-for="item in value" :key="`key-${item.suborder}`">
                {{ getSuborderInfo(item.suborder) }}
              </li>
            </ul>
          </template>
          <template v-slot:item.pk="{ value }">
            <div class="d-flex g-1 justify-content-start">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <i-btn
                    color="info"
                    icon="fa-print"
                    outlined
                    small
                    :title="$tc('print', 1, { value: $tc('payment', 1) })"
                    @click="openDetails(value)"
                    v-on="on"
                  ></i-btn>
                </template>
              </v-tooltip>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <i-btn
                    v-if="checkPermissions.cancel && checkStatusPayment(value)"
                    color="error"
                    icon="fa-times"
                    outlined
                    small
                    :title="$t('reverse_transaction')"
                    @click="reverseTransaction(value)"
                    v-on="on"
                  ></i-btn>
                </template>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
      </div>
    </div>

    <!-- Modal de Agregar nuevo tipo de pago -->
    <dial-type
      ref="dialtype"
      :to-edit="toEdit"
      @done="addType"
      v-model="dialog"
    />

    <!-- Modal de mostar detalles de pago -->
    <show-payment
      v-if="openPaymentModal"
      ref="showpayment"
      :to-edit="paymentData"
      v-model="openPaymentModal"
    />
  </div>
</template>
<script>
import DialType from '@/components/paymenttype/modal.vue'
import ShowPayment from '@/components/receivable/PaymentDetails.vue'
import { mapActions, mapGetters } from 'vuex'
export default {
  components: {
    DialType,
    ShowPayment
  },
  props: {
    balance: [String, Number],
    invoice: {
      type: Number,
      default: null
    },
    receivable: {
      type: Number,
      default: 0
    },
    amount: {
      type: Number,
      default: 0
    },
    allSuborders: {
      type: Array,
      default: () => []
    },
    subordersSelected: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      types: [],
      payments: [],
      searchType: '',
      loading: false,
      loadingTypes: false,
      loadingSubmit: false,
      payment: {
        pk: '',
        payment_number: '',
        receivable: this.receivable,
        type_payment: null,
        amount: 0,
        date: new Date().toISOString().slice(0, 10),
        canceled: false,
        reference: '',
        suborders: []
      },
      dialog: false,
      toEdit: null,
      openReverseModal: false,
      openPaymentModal: false,
      paymentData: null
    }
  },
  computed: {
    ...mapGetters({
      getPermissions: 'session/getPermissions',
      isAdmin: 'session/isAdmin'
    }),
    /**
     * Determina si el usuario tiene los permisos necesarios según la indicación
     *
     * @returns {Object} Objeto con los permisos del usuario.
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    checkPermissions() {
      return {
        add: this.getPermissions([`invoices.add_payment`]),
        cancel: this.getPermissions([`invoices.change_payment`]),
        addNewType: this.getPermissions([`invoices.add_paymenttype`])
      }
    },
    headers() {
      return [
        {
          align: 'center',
          text: '-',
          value: 'canceled'
        },
        {
          text: this.$tc('payment', 1),
          value: 'payment_number'
        },
        {
          text: this.$t('paymenttype'),
          value: 'showType'
        },
        {
          text: this.$tc('date', 1),
          value: 'date'
        },
        {
          text: this.$tc('amount', 1),
          value: 'amount'
        },
        {
          text: this.$tc('reference', 1),
          value: 'reference'
        },
        {
          text: this.$tc('suborder', 1),
          value: 'allocations'
        },
        {
          text: this.$tc('action', 1),
          value: 'pk'
        }
      ]
    }
  },
  methods: {
    ...mapActions({
      showConfirmation: 'confirmation/confirmationValue'
    }),
    /**
     * Método que consulta los tipos de pagos que se muestran en la lista
     * desplegable de pagos.
     *
     * @returns {Array} Listado de tipo de pagos
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    async getPaymentType() {
      this.loadingTypes = true
      try {
        const query = await this.$api.invoice.type.list({})
        this.types = query.data.results
      } catch (error) {
        console.error(error)
      } finally {
        this.loadingTypes = false
      }
    },
    /**
     * Método que consulta los tipos de pagos que se muestran en la lista
     * desplegable de pagos.
     * @param {Number} receivablePK ID de la cuenta por cobrar
     *
     * @returns {Array} Listado de pagos asociados a la cuenta por cobrar
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    async getPayments(receivablePK) {
      this.loading = true
      try {
        const query = await this.$api.invoice.payment.show({
          pk: receivablePK,
          opt: {
            params: {
              receivable: true
            }
          }
        })
        this.payments = query.data
      } catch (error) {
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    /**
     * Método que se encarga de buscar una suborden específica del listado de
     * todas las que le pertenecen a la cuenta por cobrar y devuelve un payload
     * en lenguaje natural.
     *
     * @param {Number} pk ID de la suborden de venta
     *
     * @returns {String} Código y nombre de la subórdenes de venta.
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    getSuborderInfo(pk) {
      const filter = this.allSuborders.find((suborder) => suborder.pk === pk)
      if (filter) {
        return `${filter.code} ${filter.name}`
      }
      return `${this.$tc('suborder', 1)} ${pk}`
    },
    /**
     * Método que suma los pagos asociados a una suborden
     *
     * @returns {Number}
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    sumPayments(allocations = []) {
      const sum = allocations.reduce(
        (accumulator, currentValue) => accumulator + Number(currentValue.debt),
        0
      )
      return sum
    },
    /**
     * Método que valida si el formulario está lleno adecuadamente y luego
     * envía el formulario
     *
     * @returns {Void}
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    validateForm() {
      this.$refs.form.validate().then((success) => {
        if (success) {
          this.submit()
        }
      })
    },
    /**
     * Método envía el formulario que crea los pagos
     *
     * @returns {Void}
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    async submit() {
      this.payment.suborders = []
      let amount = this.payment.amount
      this.subordersSelected.forEach((element) => {
        const debt = element.total - this.sumPayments(element.allocations)
        let total = amount > debt ? debt : amount
        let format = {
          id: element.pk,
          debt: total
        }
        amount -= debt
        this.payment.suborders.push(format)
      })
      try {
        this.loadingSubmit = true
        await this.$api.invoice.paymentReceivable.create({
          pk: this.receivable,
          form: this.payment
        })
        this.resetForm()
        this.$emit('paymentCreated')
        this.$toast.success(
          `${this.$tc('payment', 1)} ${this.$tc('created', 2)}`
        )
      } catch (error) {
        console.error(error)
      } finally {
        this.getPayments(this.receivable)
        this.loadingSubmit = false
      }
    },
    /**
     * Método abre la modal de agregar nuevo método de pago
     *
     * @returns {Void}
     */
    addPaymentType(item) {
      this.toEdit = item === undefined ? null : item
      this.dialog = true
    },
    /**
     * Método agrega el nuevo tipo de pago al listado del select de métodos de
     * pago.
     *
     * @returns {Void}
     */
    addType(evt) {
      this.types.push(evt)
      this.payment.type_payment = evt.pk
    },
    /**
     * Método que resetea la información del formulario.
     *
     * @returns {Void}
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    resetForm() {
      this.payment = {
        pk: '',
        payment_number: '',
        receivable: this.receivable,
        type_payment: null,
        amount: 0,
        date: new Date().toISOString().slice(0, 10),
        canceled: false,
        reference: ''
      }
      this.$refs.form.reset()
    },
    /**
     * Método que se encarga de manejar el formulario que anula un pago
     *
     * @param {Number} item   ID del pago
     * @returns {Void}
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    reverseTransaction(item) {
      const paymentPK = item
      this.$swal({
        title: this.$t('reverse_transaction'),
        icon: 'question',
        background: 'var(--v-auxbg-base)',
        color: 'var(--v-auxtext-base)',
        showCancelButton: true,
        cancelButtonText: this.$t('cancel'),
        confirmButtonColor: 'var(--v-primary-base)',
        confirmButtonText: this.$tc('send', 0),
        input: 'textarea',
        inputPlaceholder: this.$t('reason'),
        inputValidator: (value) => {
          if (!value) return this.$t('cancel_quotation_validation')
        }
      }).then((result) => {
        if (result.isConfirmed) {
          this.submitReverse(paymentPK, result.value)
        }
      })
    },
    /**
     * Método que se encarga de enviar la razón de anulación de un pago y de
     * recargar tanto las subórdenes como los pagos.
     *
     * @param {Number} item   ID del pago
     * @returns {Void}
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    async submitReverse(pk, reason) {
      try {
        this.loading = true
        await this.$api.invoice.paymentReceivable.cancel({
          pk: pk,
          form: { reason: reason }
        })
        this.$toast.success(`${this.$tc('payment', 1)} ${this.$t('voided')}`)
        this.$emit('paymentCreated')
        this.getPayments(this.receivable)
      } catch (error) {
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    /**
     * Método que verifica si un pago está cancelado.
     *
     * @param {Number} pk   ID del pago
     * @returns {Boolean}
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    checkStatusPayment(paymentPK) {
      const NoCanceled = this.payments.find((payment) => {
        return payment.pk === paymentPK && payment.canceled === false
      })
      return NoCanceled ? true : false
    },
    /**
     * Método que se encarga de manejar la data que se mostrará en la modal de
     * detalles de pago.
     *
     * @param {Number} pk   ID del pago
     * @returns {Void}
     *
     * @author Angelo Osorio <danielking.321 at gmail.com>
     */
    openDetails(pk) {
      this.paymentData = this.payments.find((payment) => {
        return payment.pk === pk
      })
      this.paymentData.suborders = []
      this.paymentData.allocations.forEach((suborder) => {
        this.paymentData.suborders.push(this.getSuborderInfo(suborder.suborder))
      })
      this.openPaymentModal = true
    }
  },
  mounted() {
    this.getPayments(this.receivable)
    this.getPaymentType()
  },
  watch: {
    amount: {
      handler(newValue) {
        this.payment.amount = newValue
      },
      immediate: true
    }
  }
}
</script>
<style lang="sass">
.payment-container
  // -------------------------
  // Contenedor del componente
  // -------------------------
  background-color: rgba(132, 132, 132, 0.1)
  border-left: 10px solid var(--v-primary-base)
  border-radius: 10px
  padding: 10px

.payment-form
  // ------------------------------------------------------------
  // Contenedor del formulario y el listado de cuentas por cobrar
  // ------------------------------------------------------------
  display: flex
  flex-direction: column
  @media (width >= 960px)
    display: grid
    grid-template-columns: 400px 1fr
    align-items: start
    gap: 10px

.v-data-table
  tbody > tr > td
    .v-btn.v-size--small
      .v-icon.v-icon
        font-size: 16px!important
</style>
